import {
  column,
  ContentHeader,
  GridColumn,
  GridSortConfig,
  GridTableLayout,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useGridTableApi,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostConfigurationSummaryOrderBy, Order } from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { createExportCsvUrl } from "src/RouteUrls";
import { parseOrder, toOrder } from "src/utils/ordering";
import { openNewTab } from "src/utils/window";
import {
  AllConfigurations_CostConfigurationSummaryFragment,
  AllConfigurations_PotentialBidPlanDimensionResultFragment,
  AllConfigurationsQuery,
  useAllConfigurationsDimensionsQuery,
  useAllConfigurationsQuery,
} from "./AllConfigurationsTab.generated";
import { ReloadCostsBanner } from "./components/ReloadCostsBanner";
import {
  ALL_CONFIGURATIONS_PAGE_SIZE,
  AllConfigurationsFilter,
  createFilterDefs,
  createMetricColumns,
  mapToFilter,
  typeDimensions,
} from "./utils";

type AllConfigurationsTabProps = { productOfferings: NamedFragment[] };

export function AllConfigurationsTab({ productOfferings }: AllConfigurationsTabProps) {
  const { id: projectId } = useProjectContext();
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const dimensionsQuery = useAllConfigurationsDimensionsQuery({
    variables: { productOfferingIds },
    skip: productOfferingIds.isEmpty,
  });
  const dimensions = useMemo(() => dimensionsQuery.data?.potentialBidPlanDimensions ?? [], [dimensionsQuery.data]);
  const filterDefs = useMemo(() => createFilterDefs(productOfferings, dimensions), [productOfferings, dimensions]);
  const columns = useMemo(() => createColumns(dimensions), [dimensions]);
  const api = useGridTableApi<Row>();
  const layoutState = useGridTableLayoutState<AllConfigurationsFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_costAnalysisAllConfigurationsFilter` },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<CostConfigurationSummaryOrderBy>(defaultAllConfigurationsOrderBy);
  const filter = mapToFilter(layoutState.filter, layoutState.searchString, productOfferingIds);
  const query = useAllConfigurationsQuery({
    variables: { filter, orderBy, page: { offset: 0, limit: ALL_CONFIGURATIONS_PAGE_SIZE } },
    skip: productOfferingIds.isEmpty,
  });
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const createRows = useCallback(
    (data: AllConfigurationsQuery | undefined) => [
      simpleHeader,
      ...simpleDataRows(data?.costConfigurationSummaries.entities),
    ],
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    const page = query.data?.costConfigurationSummaries;
    if (page?.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: page.entities.length, limit: ALL_CONFIGURATIONS_PAGE_SIZE } },
      });
    }
  }, [query]);
  return (
    <>
      <ReloadCostsBanner productOfferingIds={productOfferingIds} onReload={() => query.refetch()} />
      <ContentHeader
        title="All Configurations"
        description="Compare total costs across all configurations in this community. Costs recalculate automatically whenever a cost-changing edit is saved."
        actions={[
          {
            label: "Export",
            icon: "download",
            variant: "secondary",
            onClick: () => {
              // The offering and dimensions are always exported; the metric columns follow the table
              const shown = api.getVisibleColumnIds().filter((id) => columns.find((c) => c.id === id)?.canHide);
              const params = new URLSearchParams({
                filter: JSON.stringify(filter),
                orderBy: JSON.stringify(orderBy),
                columns: shown.join(","),
              });
              openNewTab(`${createExportCsvUrl("costConfigurationSummaries")}&${params}`);
            },
          },
        ]}
      />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="There aren't any configurations to display."
        tableProps={{
          api,
          query,
          createRows,
          columns,
          sorting,
          as: "virtual",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </>
  );
}

export const defaultAllConfigurationsOrderBy: CostConfigurationSummaryOrderBy = { productOffering: Order.Asc };

type Row = SimpleHeaderAndData<AllConfigurations_CostConfigurationSummaryFragment>;

function createColumns(dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[]): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "productOffering",
      name: "Product Offering",
      header: "Product Offering",
      serverSideSortKey: "productOffering",
      canHide: false,
      data: ({ productOffering }) => productOffering.name,
      mw: "200px",
    }),
    ...typeDimensions(dimensions).map(({ type, displayName }) =>
      column<Row>({
        id: type.id,
        name: displayName,
        header: displayName,
        canHide: false,
        data: ({ pinnedOptions }) => pinnedOptions.find((rpo) => rpo.type.id === type.id)?.name,
      }),
    ),
    ...createMetricColumns<AllConfigurations_CostConfigurationSummaryFragment>((ccs) => ccs, true),
  ];
}
